import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { API_URL } from '../../../settings/local_settings';
import { APIClient } from '../../../utils/APIClient';
import { Spinner } from '../../Spinner';
import CancelSubscriptionModal from './CancelSubscriptionModal';
import { formatSubscriptionDate } from './subscriptionDates';

const LABEL_STYLE = {
    fontFamily: 'Source Sans Pro, sans-serif',
    fontWeight: 600,
    fontSize: '0.87em',
    color: '#4A4A4A'
};

const DESCRIPTION_STYLE = {
    margin: '0.2em 0 0',
    color: '#B6B6B7',
    fontSize: '0.87em',
    fontFamily: 'Source Sans Pro, sans-serif'
};

/**
 * Bloque "Cancelar suscripción" del final de la pantalla.
 *
 * Tiene DOS caras, y la segunda es la que faltaba: si la agencia ya pidió la baja, en lugar del
 * botón rojo se muestra el estado de su solicitud. Antes el botón quedaba igual que siempre, así
 * que quien había cancelado volvía al día siguiente, no encontraba ningún rastro y concluía —con
 * razón— que no había pasado nada.
 */
export function CancelSubscriptionSection({ isFreeTrial, subscriptionData, cancellationRequest,
                                            onCancellationChanged, onGoToPlans, variant, onOpenCancel,
                                            onRetentionAccepted }) {
    const { t } = useTranslation();
    const [withdrawing, setWithdrawing] = useState(false);

    if (isFreeTrial) {
        return null;
    }

    function handleWithdraw() {
        setWithdrawing(true);
        new APIClient(API_URL + 'agency/subscription/cancel/withdraw/')
            .call('POST', {})
            .then((data) => {
                setWithdrawing(false);
                if (data.status === 'OK') {
                    if (onCancellationChanged) {
                        onCancellationChanged(null);
                    }
                } else {
                    // Mismo criterio que el modal: toast (no `alert()`) y sin interpolar el
                    // `error_message` del backend.
                    toast.error(t('agency_subscription.withdraw_error'));
                }
            });
    }

    const requestedOn = cancellationRequest
        && formatSubscriptionDate(cancellationRequest.created_at);

    if (variant === 'link') {
        return (
            <LinkVariant t={t} cancellationRequest={cancellationRequest} requestedOn={requestedOn}
                         withdrawing={withdrawing} onWithdraw={handleWithdraw} subscriptionData={subscriptionData}
                         onCancellationChanged={onCancellationChanged} onGoToPlans={onGoToPlans}
                         onOpenCancel={onOpenCancel} onRetentionAccepted={onRetentionAccepted}/>
        );
    }

    return (
        <div className="box" style={{ marginBottom: '1.5em' }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1.5em',
                padding: '1.1em'
            }}>
                <div>
                    <span style={LABEL_STYLE}>
                        {cancellationRequest
                            ? t('agency_subscription.cancellation_pending_label')
                            : t('agency_subscription.cancel_subscription_label')}
                    </span>
                    <p style={DESCRIPTION_STYLE}>
                        {cancellationRequest
                            ? t('agency_subscription.cancellation_pending_description', {
                                date: requestedOn,
                                interpolation: { escapeValue: false }
                            })
                            : t('agency_subscription.cancel_subscription_description')}
                    </p>
                </div>
                {cancellationRequest && (
                    withdrawing
                        ? <Spinner color="positive small" />
                        : (
                            <button className="btn -negative" onClick={handleWithdraw}>
                                {t('agency_subscription.withdraw_cancellation')}
                            </button>
                        )
                )}
                {/* El modal se monta SIEMPRE, en una posición fija del árbol: lo que se esconde es
                    su disparador. Cuando vivía dentro de un ternario junto al botón de retirar,
                    confirmar la baja lo desmontaba en el mismo tick —el padre setea
                    `cancellationRequest`, la sección cambia de rama— y la pantalla de éxito no
                    llegaba a verse NUNCA: el modal desaparecía de golpe, sin el tilde ni la línea
                    de "tu suscripción sigue activa", que es justo lo que este bloque vino a
                    resolver. */}
                <CancelSubscriptionModal
                    subscriptionData={subscriptionData}
                    hideTrigger={!!cancellationRequest}
                    onCancellationRequested={onCancellationChanged}
                    onGoToPlans={onGoToPlans}
                    onRetentionAccepted={onRetentionAccepted}
                />
            </div>
        </div>
    );
}

/**
 * La cara de la vista de la agencia paga (tanda «Suscripción y pagos»): sin solicitud, un link discreto al
 * final («Dar de baja la suscripción») que abre el modal de siempre; con una en curso, el bloque de siempre
 * con «Retirar la solicitud», en el lugar del link.
 *
 * La estructura es FIJA y el modal va siempre en la misma posición, por lo mismo que la otra cara: si
 * confirmar la baja lo desmontara (el padre setea `cancellationRequest` en el mismo tick), la pantalla de
 * éxito no se vería nunca.
 */
function LinkVariant({ t, cancellationRequest, requestedOn, withdrawing, onWithdraw, subscriptionData,
                       onCancellationChanged, onGoToPlans, onOpenCancel, onRetentionAccepted }) {
    return (
        <div className={cancellationRequest ? 'sp-cancel -pending' : 'sp-cancel'}>
            {cancellationRequest && (
                <div className="sp-cancel__text">
                    <p className="sp-cancel__title">{t('agency_subscription.cancellation_pending_label')}</p>
                    <p className="sp-cancel__desc">
                        {t('agency_subscription.cancellation_pending_description', {
                            date: requestedOn,
                            interpolation: { escapeValue: false }
                        })}
                    </p>
                </div>
            )}
            {cancellationRequest && (
                withdrawing
                    ? <Spinner color="positive small" />
                    : (
                        <button type="button" className="btn -negative" onClick={onWithdraw}>
                            {t('agency_subscription.withdraw_cancellation')}
                        </button>
                    )
            )}
            <CancelSubscriptionModal
                subscriptionData={subscriptionData}
                hideTrigger={!!cancellationRequest}
                onCancellationRequested={onCancellationChanged}
                onGoToPlans={onGoToPlans}
                onOpen={onOpenCancel}
                onRetentionAccepted={onRetentionAccepted}
                triggerLabel={t('subscription_page.cancel_link')}
                triggerClassName="sp-linkbtn -quiet"
                modalClassName="sp-modal"
            />
        </div>
    );
}
